import { Page } from "../../../components/page-base";
import { AppDataGrid } from "../../../components/datagrid/AppDataGrid";
import { RowActionButtons } from "../../../components/datagrid/RowActionButtons";
import { NameCell } from "../../../components/datagrid/NameCell";
import { StatusChip } from "../../../components/datagrid/StatusChip";
import {
  BulkActionsBar,
  BulkAction,
} from "../../../components/datagrid/BulkActionsBar";
import type {
  DataGridColumn,
  DataGridRow,
  FilterChip,
} from "../../../components/datagrid/types";
import SearchIcon from "@mui/icons-material/Search";
import {
  ChipFilterConfig,
  FilterDrawer,
} from "../../../components/filter-drawer";
import { BtnContainer, Container } from "./styles";
import { isMobile } from "is-mobile";
import { useState, useMemo, useEffect, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import ModeEditOutlineIcon from "@mui/icons-material/ModeEditOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { LevelChip } from "../../../components/datagrid/LevelChip";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { SellerFormModal } from "../components/seller-form-modal";
import { ConfirmationModal } from "../../../components/confirmation-modal";
import { toast } from "react-toastify";
import useUserStore from "../../../store/user";
import { SellersService } from "../services";
import { buildSellerPayload, type SellerFormData } from "./seller-payload";
import { hideImporterAccounts } from "./seller-visibility";
export interface Seller {
  id: string;
  name: string;
  email: string;
  level:
    | "Cliente"
    | "Supervisor de Contas"
    | "Conta Google (importador)"
    | "Admin"
    | "Vendedor";

  // O que a lista realmente grava (e o que o StatusChip pinta). O tipo dizia
  // "active"/"inactive", que nunca chegou a ser usado aqui - por isso o modal
  // de edicao precisava de cast para ler os dois formatos.
  status: "success" | "disabled";
  statusLabel: "Ativo" | "Inativo";
}

export type TSeller = {
  id: string;
  nome: string;
  userType: string;
  email: string;
  indDisabled: boolean;
};

type SellerFilters = {
  role: string;
  disabled: boolean | null;
};

const initialFilters: SellerFilters = {
  role: "",
  disabled: null,
};

export const Sellers = () => {
  const { user } = useUserStore();
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<(string | number)[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
  });
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [filters, setFilters] = useState(initialFilters);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSeller, setEditingSeller] = useState<Seller | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [sellerToDeleteId, setSellerToDeleteId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submittedSearch, setSubmittedSearch] = useState("");

  const parseUserType = (userType: string) => {
    switch (userType) {
      case "CUSTOMER":
        return "Cliente";
      case "PORTFOLIO_MANAGER":
        return "Supervisor de Contas";
      case "GMAIL_MANAGER":
        return "Conta Google (importador)";
      case "ADMIN":
        return "Admin";
      case "STAFF":
        return "Vendedor";
      default:
        return "";
    }
  };
  const fetchData = useCallback(async () => {
    if (!user?.token) {
      toast.warn("Sessão expirada. Faça login novamente.");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const params = {
        token: user.token,
        query: submittedSearch,
        role: filters.role === "" ? undefined : filters.role,
        disabled: filters.disabled,
      };

      const response = await SellersService.getSellers(params);
      const visibleResponse = hideImporterAccounts<TSeller>(
        response || [],
        filters.role,
      );

      if (visibleResponse.length > 0) {
        const mappedProfiles: Seller[] = visibleResponse.map((p: TSeller) => ({
          id: p.id,
          name: p.nome,
          email: p.email,
          // parseUserType devolve "" para nivel desconhecido (ex.: PLATFORM_READER),
          // e o chip fica vazio. Comportamento antigo, mantido de proposito aqui.
          level: parseUserType(p.userType) as Seller["level"],
          status: p.indDisabled ? "disabled" : "success",
          statusLabel: p.indDisabled ? "Inativo" : "Ativo",
        }));
        setSellers(mappedProfiles);
        setPagination((prev) => ({
          ...prev,
          totalCount: visibleResponse.length,
        }));
      } else {
        setSellers([]);
        setPagination((prev) => ({ ...prev, totalCount: 0 }));
      }
    } catch (error) {
      console.error("Erro ao buscar perfis:", error);
      toast.error("Falha ao carregar perfis. Tente novamente.");
      setSellers([]);
    } finally {
      setLoading(false);
    }
  }, [user?.token, submittedSearch, filters.role, filters.disabled]);
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = useCallback((page: number) => {
    setPagination((p) => ({ ...p, page }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination((p) => ({ ...p, pageSize, page: 1 }));
  }, []);

  const handleOpenCreateModal = () => {
    setEditingSeller(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = useCallback((id: string) => {
    const sellerToEdit = sellers.find((s) => s.id === id);
    if (sellerToEdit) {
      setEditingSeller(sellerToEdit);
      setIsModalOpen(true);
    }
  }, [sellers]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingSeller(null);
  };

  const handleSaveSeller = async (sellerData: SellerFormData) => {
    const payload = buildSellerPayload(sellerData);

    if (sellerData.id) {
      await SellersService.updateSeller(
        sellerData.id,
        payload,
        user?.token || "",
      );
      toast.success("Usuário atualizado com sucesso");
    } else {
      const res = await SellersService.createSeller(payload, user?.token || "");
      console.log(res);
      toast.info(res.data.message || "Usuário criado com sucesso");
    }

    await fetchData();
    handlePageChange(1);
    handleCloseModal();
  };

  const handleDeleteClick = useCallback((id: string) => {
    setSellerToDeleteId(id);
    setIsDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = async () => {
    if (sellerToDeleteId !== null) {
      await SellersService.deleteUser(sellerToDeleteId, user?.token || "");
    }
    await fetchData();
    handlePageChange(1);
    setIsDeleteModalOpen(false);
    setSellerToDeleteId(null);
    toast.success("Vendedor excluído com sucesso");
  };

  const handleSelectRow = (rowId: string | number) => {
    setSelectedRowIds((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId],
    );
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedRowIds(checked ? sellers.map((s) => s.id) : []);
  };

  const exportFile = () => {
    const selectedSellers = sellers.filter((seller) =>
      selectedRowIds.includes(seller.id),
    );
    console.log(selectedSellers);
    setSelectedRowIds([]);
  };

  const columns: DataGridColumn[] = [
    { id: "name", header: "Nome", minWidth: 220 },
    { id: "email", header: "Email", minWidth: 240 },
    { id: "level", header: "Nível", minWidth: 150 },
    { id: "status", header: "Status", minWidth: 100 },
    { id: "actions", header: "Ações", minWidth: 100 },
  ];

  const rows: DataGridRow[] = useMemo(() => {
    const start = (pagination.page - 1) * pagination.pageSize;
    const end = start + pagination.pageSize;
    const paginatedSellers = sellers.slice(start, end);

    return paginatedSellers.map((seller) => ({
      id: seller.id,
      render: {
        name: <NameCell primaryText={seller.name} />,
        email: (
          <Typography variant="body2" color="text.secondary">
            {seller.email}
          </Typography>
        ),
        level: <LevelChip level={seller.level} />,
        status: (
          <StatusChip status={seller.status} label={seller.statusLabel} />
        ),
        actions: (
          <RowActionButtons
            actions={[
              {
                icon: <ModeEditOutlineIcon fontSize="small" />,
                tooltip: "Editar",
                onClick: () => handleOpenEditModal(seller.id),
              },
              {
                icon: (
                  <DeleteOutlineIcon
                    fontSize="small"
                    sx={{ color: "#D93025" }}
                  />
                ),
                tooltip: "Excluir",
                onClick: () => handleDeleteClick(seller.id),
              },
            ]}
          />
        ),
      },
    }));
  }, [sellers, pagination.page, pagination.pageSize, handleOpenEditModal, handleDeleteClick]);

  const filterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];
    if (filters.role && filters.role !== "") {
      chips.push({
        id: "role",
        label: `Nível de acesso: ${parseUserType(filters.role)}`,
        onDelete: () => handleFilterChange("role", ""),
      });
    }
    if (filters.disabled !== null) {
      chips.push({
        id: "disabled",
        label: `Status: ${filters.disabled ? "Desativado" : "Ativado"}`,
        onDelete: () => handleFilterChange("disabled", null),
      });
    }
    return chips;
  }, [filters]);

  const handleFilterChange = (field: string, value: unknown) => {
    if (field === "role" && typeof value === "string") {
      setFilters((prev) => ({ ...prev, role: value }));
    }
    if (field === "disabled" && (typeof value === "boolean" || value === null)) {
      setFilters((prev) => ({ ...prev, disabled: value }));
    }
    // Aqui você chamaria a API para buscar os dados filtrados
    console.log(`Filtering by ${field}: ${value}`);
  };
  const handleClearFilters = () => {
    setFilters(initialFilters);
    // Aqui você chamaria a API para buscar os dados sem filtros
    console.log("Clearing filters");
  };

  const chipFilters: ChipFilterConfig[] = [
    {
      id: "role",
      label: "Nível de Acesso",
      options: [
        { label: "Todos", value: "" },
        { label: "Cliente", value: "CUSTOMER" },
        { label: "Supervisor de Contas", value: "PORTFOLIO_MANAGER" },
        { label: "Conta Google (importador)", value: "GMAIL_MANAGER" },
        { label: "Admin", value: "ADMIN" },
        { label: "Vendedor", value: "STAFF" },
      ],
    },
    {
      id: "disabled",
      label: "Status",
      options: [
        { label: "Todos", value: null },
        { label: "Ativado", value: false },
        { label: "Desativado", value: true },
      ],
    },
  ];

  const handleSearchClick = useCallback(() => {
    // aplica a busca e volta para a primeira página
    setPagination((p) => ({ ...p, page: 1 }));
    setSubmittedSearch(searchTerm);
  }, [searchTerm]);

  return (
    <Page.Root>
      <Container id="dashboard-container">
        <Page.HeaderAux />
        <BtnContainer>
          <Page.Header
            title="Lista de Usuários"
            subtitle="Gerenciar usuários do sistema. Contas Google (importador) não aparecem aqui — elas só importam perfis, não acessam o sistema; para vê-las, use Filtros › Nível de Acesso."
          />
          <div className="btn">
            <Page.Button
              label="Novo usuário"
              bgColor="#1264C7"
              hoverBgcolor="#1d4ed8"
              startIcon={<AddOutlinedIcon />}
              onClick={handleOpenCreateModal}
            />
          </div>
        </BtnContainer>
        <Box
          sx={{ width: "88%", marginBottom: isMobile() ? "6vh" : "1px", mt: 3 }}
        >
          <AppDataGrid
            columns={columns}
            rows={rows}
            tableLabel="usuários"
            selectedRowIds={selectedRowIds}
            onSelectRow={handleSelectRow}
            onSelectAll={handleSelectAll}
            onOpenFilters={() => setIsFilterDrawerOpen(true)}
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            searchPlaceholder="Buscar usuário..."
            filterChips={filterChips}
            filterCount={filterChips.length}
            onSearchClick={handleSearchClick}
            pagination={{
              ...pagination,
              onPageChange: handlePageChange,
              onPageSizeChange: handlePageSizeChange,
            }}
            bulkActionsBarRenderer={(selectedIds) => {
              const bulkActions: BulkAction[] = [
                {
                  label: "Ativar",
                  onClick: exportFile,
                },
                {
                  label: "Desativar",
                  onClick: exportFile,
                },
                {
                  label: "Cancelar",
                  onClick: () => setSelectedRowIds([]),
                  color: "secondary",
                },
              ];
              return (
                <BulkActionsBar
                  selectedCount={selectedIds.length}
                  tableLabel="usuário"
                  actions={bulkActions}
                />
              );
            }}
            noDataIcon={<SearchIcon sx={{ fontSize: 48 }} />}
            noDataTitle="Nenhum usuário encontrado"
            noDataDescription="Ajuste sua busca ou adicione novos usuários."
            isLoading={loading}
          />
        </Box>
        <FilterDrawer
          open={isFilterDrawerOpen}
          onClose={() => setIsFilterDrawerOpen(false)}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          chipFilters={chipFilters}
        />
        <SellerFormModal
          open={isModalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveSeller}
          seller={editingSeller}
        />
        <ConfirmationModal
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleConfirmDelete}
          title="Confirmar Exclusão"
          message="Tem certeza de que deseja excluir este usuário? Esta ação não pode ser desfeita."
          confirmButtonText="Excluir"
        />
      </Container>
    </Page.Root>
  );
};
